import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, Grid, Stack, Chip, CircularProgress, Avatar, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { toast } from 'react-toastify';
import { getPoojaBookingDetail, updatePoojaBookingStatus, assignPoojaAstrologer } from '../../api/endpoints/pooja';
import { getAstrologers } from '../../api/endpoints/astrologers';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import PoojaOrderCodes from '../../components/PoojaOrderCodes';

const statusColor = { pending: 'warning', confirmed: 'info', completed: 'success', cancelled: 'error' };

const PoojaOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [astrologers, setAstrologers] = useState([]);
  const [selectedAstrologer, setSelectedAstrologer] = useState('');
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getPoojaBookingDetail(id);
        setBooking(res.data.data);
        setSelectedAstrologer(res.data.data.astrologerId?._id || '');
      } catch {
        toast.error('Booking not found');
        navigate('/pooja-orders');
      } finally {
        setLoading(false);
      }
    };
    fetch();
    getAstrologers({ limit: 200 }).then(res => setAstrologers(res.data.data || [])).catch(() => {});
  }, [id, navigate]);

  const handleStatusUpdate = async (status) => {
    setUpdating(true);
    try {
      const res = await updatePoojaBookingStatus(id, status);
      setBooking(res.data.data);
      toast.success(`Booking ${status}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleAssignAstrologer = async () => {
    if (!selectedAstrologer) { toast.error('Select an astrologer'); return; }
    setAssigning(true);
    try {
      const res = await assignPoojaAstrologer(id, selectedAstrologer);
      setBooking(res.data.data);
      toast.success('Astrologer assigned');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign astrologer');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!booking) return null;

  const astro = booking.astrologerId;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon sx={{ fontSize: 28 }} />} onClick={() => navigate('/pooja-orders')} sx={{ mb: 2 }}>Back to Orders</Button>
      <Typography variant="h5" fontWeight={700} gutterBottom>Booking Detail</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight={600}>{booking.pujaName || booking.pujaType}</Typography>
                  <Chip label={booking.status} color={statusColor[booking.status] || 'default'} />
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#FAFAFA', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <PoojaOrderCodes booking={booking} />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Puja Type</Typography>
                    <Typography variant="body2" fontWeight={500}>{booking.pujaType}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Amount</Typography>
                    <Typography variant="body2" fontWeight={600} color="error.main">{formatCurrency(booking.amount)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Scheduled Date</Typography>
                    <Typography variant="body2" fontWeight={500}>{booking.date || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Scheduled Time</Typography>
                    <Typography variant="body2" fontWeight={500}>{booking.time || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Booked On</Typography>
                    <Typography variant="body2">{formatDateTime(booking.createdAt)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Booking ID</Typography>
                    <Typography variant="body2" fontFamily="monospace">{booking._id}</Typography>
                  </Grid>
                </Grid>
                {booking.notes && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1 }}>{booking.notes}</Typography>
                  </Box>
                )}
                {booking.participants?.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Participants</Typography>
                    {booking.participants.map((p, i) => (
                      <Typography key={i} variant="body2">{p.name}{p.gotra ? ` (${p.gotra})` : ''}</Typography>
                    ))}
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Customer</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: '#c2185b' }}>{booking.userId?.name?.[0]?.toUpperCase() || '?'}</Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{booking.userId?.name || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{booking.userId?.email}</Typography>
                  <Typography variant="caption" color="text.secondary">{booking.userId?.phone}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Assigned Astrologer</Typography>
              {astro ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                  <Avatar src={astro.avatar} sx={{ width: 44, height: 44 }}>
                    {astro.name?.[0]?.toUpperCase() || '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{astro.name}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">{astro.email}</Typography>
                    <Typography variant="caption" color="text.secondary">{astro.phone}</Typography>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No astrologer assigned</Typography>
              )}
            </CardContent>
          </Card>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Assign Astrologer</Typography>
              <Stack spacing={1}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Select Astrologer</InputLabel>
                  <Select value={selectedAstrologer} label="Select Astrologer" onChange={(e) => setSelectedAstrologer(e.target.value)}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {astrologers.map((a) => (
                      <MenuItem key={a._id} value={a.userId?._id || a._id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={a.userId?.avatar} sx={{ width: 24, height: 24, fontSize: 12 }}>{a.userId?.name?.[0]}</Avatar>
                          <span>{a.userId?.name}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="contained" size="small" fullWidth startIcon={<PersonAddIcon />}
                  onClick={handleAssignAstrologer} disabled={assigning || !selectedAstrologer}>
                  {assigning ? 'Assigning...' : 'Assign Astrologer'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Update Status</Typography>
              <Stack spacing={1}>
                {['confirmed', 'completed', 'cancelled'].map((s) => (
                  <Button key={s} variant="outlined" size="small" fullWidth
                    onClick={() => handleStatusUpdate(s)} disabled={updating || booking.status === s}
                    color={s === 'cancelled' ? 'error' : s === 'completed' ? 'success' : 'info'}
                  >
                    {updating ? 'Updating...' : `Mark ${s.charAt(0).toUpperCase() + s.slice(1)}`}
                  </Button>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PoojaOrderDetail;
